import React, { lazy, Suspense, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { WhatsAppWidget } from '../components/WhatsAppWidget';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Lazy load heavy components for better performance
const ThreeBackground = lazy(() => import('../components/ThreeBackground').then(module => ({ default: module.ThreeBackground })));
const StatsSection = lazy(() => import('../components/StatsSection').then(module => ({ default: module.StatsSection })));
const WhyChooseUs = lazy(() => import('../components/WhyChooseUs').then(module => ({ default: module.WhyChooseUs })));
const HowItWorks = lazy(() => import('../components/HowItWorks').then(module => ({ default: module.HowItWorks })));
const KeySectors = lazy(() => import('../components/KeySectors').then(module => ({ default: module.KeySectors })));
const RecentJobs = lazy(() => import('../components/RecentJobs').then(module => ({ default: module.RecentJobs })));
const TestimonialCarousel = lazy(() => import('../components/TestimonialCarousel').then(module => ({ default: module.TestimonialCarousel })));
const FAQ = lazy(() => import('../components/FAQ').then(module => ({ default: module.FAQ })));

// Simple loading fallback
const LoadingFallback = () => (
  <div className="py-12 flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
  </div>
);

const Home = () => {
  const { user } = useAuth();

  useEffect(() => {
    // 1. Initialize Chatbase Proxy/Queue
    if (!window.chatbase || window.chatbase("getState") !== "initialized") {
      window.chatbase = (...args) => {
        if (!window.chatbase.q) { window.chatbase.q = [] }
        window.chatbase.q.push(args)
      };
      window.chatbase = new Proxy(window.chatbase, {
        get(target, prop) {
          if (prop === "q") { return target.q }
          return (...args) => target(prop, ...args)
        }
      });
    }

    // 2. Load Chatbase Script
    const loadChatbase = () => {
      if (document.getElementById("93HbE7vc5_p2EtmrSXO3u")) return;
      const script = document.createElement("script");
      script.src = "https://www.chatbase.co/embed.min.js";
      script.id = "93HbE7vc5_p2EtmrSXO3u";
      script.domain = "www.chatbase.co";
      script.async = true;
      document.body.appendChild(script);
    };

    if (document.readyState === "complete") {
      loadChatbase();
    } else {
      window.addEventListener("load", loadChatbase);
    }

    // 3. User Identity Verification
    if (user) {
      const identifyUser = async () => {
        try {
          const response = await api.get('/auth/chatbot-token');
          const { token } = response.data;
          window.chatbase('identify', { token });
          console.log('[Chatbase] User identified successfully');
        } catch (error) {
          console.error('[Chatbase] Identity verification failed:', error);
        }
      };
      identifyUser();
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-white relative">
      <Suspense fallback={<div className="fixed inset-0 bg-gradient-to-b from-slate-900 to-slate-950" />}>
        <ThreeBackground />
      </Suspense>
      <Navbar />
      <main className="relative z-10">
        <HeroSection />
        <Suspense fallback={<LoadingFallback />}>
          <RecentJobs />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <KeySectors />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <StatsSection />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <WhyChooseUs />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <HowItWorks />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <TestimonialCarousel />
        </Suspense>
        <Suspense fallback={<LoadingFallback />}>
          <FAQ />
        </Suspense>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default Home;
